import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Calendar, Clock, MapPin, Map, Ticket, User, Package } from 'lucide-react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { eventsAPI } from '../services/api';
import { useCart } from '../store/cartStore';
import { useAlert } from '../contexts/AlertContext';

import SeatSelectionModal from '../components/SeatSelectionModal';
import { parseEventDate } from '../utils/dateUtils';
import './EventDetailsPage.css';

const formatStatusBadge = (status) => {
  if (!status) {
    return { label: 'Inactive', color: '#ff3b30' };
  }
  const normalized = status.toUpperCase();
  if (normalized === 'ACTIVE') {
    return { label: 'Active', color: '#34c759' };
  }
  if (normalized === 'VIEW_ONLY') {
    return { label: 'View Only', color: '#ff9f0a' };
  }
  if (normalized === 'PAUSED') {
    return { label: 'Paused', color: '#ff9f0a' };
  }
  if (normalized === 'SOLD_OUT') {
    return { label: 'Sold Out', color: '#ff3b30' };
  }
  return { label: normalized, color: '#8e8e93' };
};

const getAvailabilityLabel = (ticketType) => {
  if (ticketType.validUntil && parseEventDate(ticketType.validUntil) < new Date()) {
    return 'Sales Ended';
  }
  if (ticketType.status === 'SOLD_OUT' || ticketType.availableCount === 0) {
    return 'Sold out';
  }
  if (ticketType.status === 'PAUSED') {
    return 'Not available';
  }
  if (ticketType.availableCount === 1) {
    return 'Last one';
  }
  if (ticketType.availableCount <= 5) {
    return `Only ${ticketType.availableCount} left`;
  }
  return `${ticketType.availableCount} available`;
};


const EventDetailsPage = () => {
  const { id: eventId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { showAlert } = useAlert();
  const {
    cartItems,
    addToCart,
    removeSpecificSeat, // Add this
    getQuantityInCart,
    updateQuantity: updateStoreQuantity, // Alias store function
    getCartTotal
  } = useCart();

  const [event, setEvent] = useState(null);
  const [ticketTypes, setTicketTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);



  const [activeSeatSelection, setActiveSeatSelection] = useState(null);

  const [isPurchaseBarFixed, setIsPurchaseBarFixed] = useState(true);
  const eventContainerRef = React.useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      if (!eventContainerRef.current) return;
      const { bottom } = eventContainerRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      // If the container bottom is below the window bottom, bar should be fixed
      // When we scroll past, bottom < windowHeight, so it becomes absolute
      setIsPurchaseBarFixed(bottom > windowHeight);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Check on mount
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);



  const venueLabel = event?.venueName || event?.venue?.name || 'Venue to be announced';

  const venuePlaceId = event?.venuePlaceId
    || event?.venue?.address?.placeId
    || event?.venue?.placeId
    || event?.placeId
    || null;

  const venueAddress =
    event?.venueFormattedAddress
    || event?.venue?.address?.formattedAddress
    || [
      event?.venue?.address?.streetAddress,
      event?.venue?.address?.city,
      event?.venue?.address?.stateProvince,
      event?.venue?.address?.country
    ].filter(Boolean).join(', ')
    || null;

  const openVenueInMaps = useCallback(() => {
    const fallbackQuery = venueAddress || venueLabel || '';

    // Use the Universal Google Maps URLs standard for better cross-platform support (mobile & desktop)
    // Ref: https://developers.google.com/maps/documentation/urls/get-started
    const baseUrl = 'https://www.google.com/maps/search/?api=1';
    let mapsUrl;

    if (venuePlaceId) {
      // If we have a Place ID, use it with the query as a fallback name handling
      mapsUrl = `${baseUrl}&query=${encodeURIComponent(fallbackQuery)}&query_place_id=${encodeURIComponent(venuePlaceId)}`;
    } else {
      // Otherwise just search for the address/name
      mapsUrl = `${baseUrl}&query=${encodeURIComponent(fallbackQuery)}`;
    }

    window.open(mapsUrl, '_blank', 'noopener,noreferrer');

    if (!venuePlaceId && navigator?.clipboard?.writeText) {
      // Only copy to clipboard if we relied on a fuzzy search, as a convenience
      navigator.clipboard.writeText(fallbackQuery).catch(() => { });
    }
  }, [venuePlaceId, venueAddress, venueLabel]);

  useEffect(() => {
    // loadCartFromBackend(); // Removed as per instruction
  }, []); // Removed loadCartFromBackend from dependency array

  const loadEventDetails = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const result = await eventsAPI.getById(eventId);
      if (!result) {
        throw new Error('Event not found');
      }

      setEvent(result);
      setTicketTypes(result.ticketTypes || []);
      setLastUpdated(new Date());
    } catch (err) {
      setError(err.message || 'Failed to load event details');
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    loadEventDetails();
  }, [loadEventDetails]);

  const refreshTicketAvailability = useCallback(async () => {
    try {
      setRefreshing(true);
      const token = localStorage.getItem('authToken');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8080/api'}/events/${eventId}/ticket-types`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Cache-Control': 'no-cache'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to refresh ticket availability: ${response.status}`);
      }

      const result = await response.json();
      if (result.success && result.data) {
        setTicketTypes(result.data);
        setLastUpdated(new Date());
      }
    } catch (err) {
      console.error('Error refreshing availability:', err);
    } finally {
      setRefreshing(false);
    }
  }, [eventId]);

  const getEventImage = useCallback((eventData) => {
    if (!eventData) {
      return '/placeholder.png';
    }
    return (
      eventData.imageUrl ||
      eventData.coverImage ||
      eventData.heroImage ||
      eventData.bannerImage ||
      '/placeholder.png'
    );
  }, []);

  const currentHighlightImage = useMemo(() => getEventImage(event), [event, getEventImage]);

  const handleUpdateQuantity = async (ticketTypeId, newQuantity) => {
    const ticketType = ticketTypes.find((type) => type.id === ticketTypeId);
    if (!ticketType) {
      return;
    }

    const maxQuantity = Math.min(10, ticketType.availableCount || 0);
    const quantity = Math.max(0, Math.min(maxQuantity, newQuantity));

    try {
      await updateStoreQuantity(ticketTypeId, quantity);
      await refreshTicketAvailability();
    } catch (err) {
      console.error('Failed to update quantity:', err);
      const errorMessage = err?.message || '';
      if (errorMessage.toLowerCase().includes('log in')) {
        showAlert({
          title: 'Sign in required',
          message: 'Sign in to update your cart and hold your tickets.',
          primaryLabel: 'Go to login',
          secondaryLabel: 'Keep browsing',
          onPrimary: () => navigate('/login', { state: { from: location.pathname } })
        });
      } else {
        const lower = errorMessage.toLowerCase();
        const isHoldConflict = lower.includes('on hold') || lower.includes('hold by other');
        const title = isHoldConflict ? 'Tickets on hold' : 'Update failed';
        const message = errorMessage || (isHoldConflict
          ? 'Sorry, these tickets are temporarily on hold by other buyers. Please try again shortly.'
          : 'Failed to update quantity. Please try again.');
        showAlert({
          title,
          message
        });
      }
    }
  };



  const openSeatSelection = useCallback((ticketType) => {
    // Check if event supports Seats.io directly from event data
    console.log('OpenSeatSelection Event Data:', event);
    if (event?.seatsIoEventKey) {
      setActiveSeatSelection(ticketType);
    } else {
      showAlert({
        title: 'Seat selection unavailable',
        message: 'Seat selection is not available for this event.'
      });
    }
  }, [event, showAlert]);

  const closeSeatSelection = useCallback(() => {
    // Clear seat selections in Seats.io to prevent re-selection on modal reopen
    if (activeSeatSelection) {
      // Note: The chart instance will handle clearing selections or session logic
      setActiveSeatSelection(null);
    }
  }, [activeSeatSelection]);

  const handleSeatsConfirmed = useCallback(async (selectedSeats, holdToken) => {
    if (!activeSeatSelection || !event) return;
    const ticketType = activeSeatSelection;

    try {
      // 1. Identify currently carted seats for this ticket type
      // Filter for ALL matching items since they might be split
      const relatedCartItems = cartItems.filter(item => item.ticketTypeId === ticketType.id);
      const currentSeatIds = new Set();

      relatedCartItems.forEach(item => {
        // Handle new singular seatId (from split items)
        if (item.seatId) {
          currentSeatIds.add(item.seatId);
        }
        // Handle legacy/GA seatIds array
        else if (item.seatIds && Array.isArray(item.seatIds)) {
          item.seatIds.forEach(id => currentSeatIds.add(id));
        }
        // Fallback to labels if needed (e.g. if seatId missing)
        else if (item.seatLabels && Array.isArray(item.seatLabels)) {
          item.seatLabels.forEach(labelStr => {
            // Format is usually "seatId|Label" or just "seatId" depending on backend
            // Based on previous code, likely "seatId|Label"
            const parts = labelStr.split('|');
            if (parts.length > 0 && parts[0]) {
              currentSeatIds.add(parts[0]);
            }
          });
        }
      });

      // 2. Identify new selection IDs
      const newSeatIds = new Set(selectedSeats.map(s => s.id));

      // 3. Determine Acts
      // To Add: In new set but not in current set
      const seatsToAdd = selectedSeats.filter(s => !currentSeatIds.has(s.id));

      // To Remove: In current set but not in new set
      const seatIdsToRemove = [...currentSeatIds].filter(id => !newSeatIds.has(id));

      let processedCount = 0;
      let removedCount = 0;

      // 4. Process Removals (One by one as per current API)
      if (seatIdsToRemove.length > 0) {
        // Run sequentially to avoid race conditions or backend overwhelming
        for (const seatId of seatIdsToRemove) {
          await removeSpecificSeat(event.id, seatId);
          removedCount++;
        }
      }

      // 5. Process Additions (Batch)
      if (seatsToAdd.length > 0) {
        const ticketItem = {
          ticketTypeId: ticketType.id,
          ticketTypeName: ticketType.name,
          eventId: event.id,
          eventTitle: event.title,
          eventDate: event.startTime,
          venueName: event.venueName || event.venue?.name || 'TBD',
          quantity: seatsToAdd.length, // Batch quantity for NEW seats only
          unitPrice: ticketType.priceAmount,
          currencyCode: ticketType.currencyCode || 'USD',
          category: ticketType.category,
          selectedSeatIds: seatsToAdd.map(s => s.id), // IDs for NEW seats
          seatsIoEventKey: event.seatsIoEventKey,
          holdToken: holdToken
        };

        await addToCart(ticketItem);
        processedCount = seatsToAdd.length;
      }

      // 6. Feedback
      if (processedCount > 0 || removedCount > 0) {
        let messageParts = [];
        if (processedCount > 0) messageParts.push(`Added ${processedCount} seat(s)`);
        if (removedCount > 0) messageParts.push(`Removed ${removedCount} seat(s)`);

        showAlert({
          title: 'Cart Updated',
          message: `Successfully ${messageParts.join(' and ')}.`,
          type: 'success'
        });
      } else {
        // No changes
        showAlert({
          title: 'No Changes',
          message: 'Your cart is already up to date with this selection.',
          type: 'info'
        });
      }

      closeSeatSelection();

    } catch (err) {
      console.error('Failed to update cart:', err);
      showAlert({
        title: 'Update Failed',
        message: err.message || 'Please try again.'
      });
    }
  }, [activeSeatSelection, event, cartItems, addToCart, removeSpecificSeat, showAlert, closeSeatSelection]);



  const updateGroupQuantity = async (ticketType, newQuantity) => {
    const minQty = ticketType.groupMinQuantity || 5;
    const currentQty = getQuantityInCart(ticketType.id);

    // Logic for jumping between 0 and minQty
    let finalQty = newQuantity;

    // If increasing from 0, jump to minQty
    if (currentQty === 0 && finalQty > 0) {
      finalQty = minQty;
    }
    // If decreasing and we are at or below minQty (but not 0), jump to 0
    else if (currentQty <= minQty && finalQty < currentQty) {
      finalQty = 0;
    }
    // Ensure we don't go below minQty if we are not 0
    else if (finalQty > 0 && finalQty < minQty) {
      finalQty = minQty;
    }

    const maxQuantity = Math.min(ticketType.availableCount || 0, 50);

    finalQty = Math.max(0, Math.min(maxQuantity, finalQty));

    try {
      await updateStoreQuantity(ticketType.id, finalQty);
      await refreshTicketAvailability();
    } catch (err) {
      console.error('Failed to update group quantity:', err);
      showAlert({ title: 'Update failed', message: err.message });
    }
  };

  const buyNow = () => {
    navigate('/cart');
  };

  const getCurrencySymbol = (code) => {
    if (!code) return '';
    const normalized = code.trim().toUpperCase();
    switch (normalized) {
      case 'USD':
        return '$';
      case 'EUR':
        return '€';
      case 'GBP':
        return '£';
      case 'JPY':
        return '¥';
      case 'CAD':
        return 'C$';
      case 'AUD':
        return 'A$';
      default:
        return normalized;
    }
  };

  const originalTotal = useMemo(() => {
    if (!Array.isArray(cartItems) || cartItems.length === 0) return 0;
    return cartItems.reduce((sum, item) => {
      const matchingTicket = ticketTypes.find(tt => tt.id === item.ticketTypeId);
      const basePrice = matchingTicket?.priceAmount != null
        ? Number(matchingTicket.priceAmount)
        : Number(item.unitPrice || 0);
      return sum + basePrice * (item.quantity || 0);
    }, 0);
  }, [cartItems, ticketTypes]);

  const discountedTotal = getCartTotal();
  const hasDiscount = originalTotal > discountedTotal && discountedTotal > 0;

  if (loading) {
    return (
      <div className="event-page-shell">
        <div className="event-container">
          <div className="event-loading-state">Loading event…</div>
        </div>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="event-page-shell">
        <div className="event-container">
          <div className="event-error-state">
            <h2>Unable to load this event</h2>
            <p>{error || 'Please try again later.'}</p>
            <button type="button" onClick={() => navigate('/events')}>
              Back to events
            </button>
          </div>
        </div>
      </div>
    );
  }

  const startDateObj = event.startTime ? parseEventDate(event.startTime) : null;
  const formattedDate = startDateObj
    ? startDateObj.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
    : 'Date to be announced';

  const formattedTime = startDateObj
    ? startDateObj.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit'
    })
    : 'Time to be announced';

  const eventStatus = (event.status || '').toUpperCase();
  const isEventSalesOpen = eventStatus === 'ACTIVE';
  const isEventViewOnly = eventStatus === 'VIEW_ONLY';

  const heroDetails = [
    { label: 'Date', value: formattedDate, icon: <Calendar size={18} /> },
    { label: 'Time', value: formattedTime, icon: <Clock size={18} /> },
    { label: 'Venue', value: venueLabel, icon: <MapPin size={18} />, onClick: openVenueInMaps },
    venueAddress ? { label: 'Address', value: venueAddress, icon: <Map size={18} />, onClick: openVenueInMaps } : null,
    { label: 'Category', value: event.category || 'Live Experience', icon: <Ticket size={18} /> },
    event.ageRestriction ? { label: 'Age', value: event.ageRestriction, icon: <User size={18} /> } : null
  ].filter(Boolean);

  const activeCurrencyCode = ticketTypes.length ? ticketTypes[0]?.currencyCode || 'USD' : 'USD';
  const activeCurrencySymbol = getCurrencySymbol(activeCurrencyCode);

  return (
    <div className="event-page-shell">
      <Helmet>
        <title>{event?.title ? `${event.title} - TikPlat` : 'Event Details - TikPlat'}</title>
        <meta name="description" content={event?.description || 'Discover unforgettable events on TikPlat.'} />
      </Helmet>


      <div className="event-container" ref={eventContainerRef}>
        <section className="event-hero">
          <div
            className="event-hero-ambient"
            style={{ backgroundImage: `url(${currentHighlightImage})` }}
          />
          <div className="event-hero-grid">
            <div className="event-hero-media">
              <img src={currentHighlightImage} alt={event.title} />
              <span className="event-hero-badge">{event.category || 'Featured'}</span>
            </div>
            <div className="event-hero-body">
              <h1 className="event-hero-title">{event.title}</h1>
              {event.description && <p className="event-hero-description">{event.description}</p>}
              <div className="event-hero-details">
                {heroDetails.map(detail => {
                  const isClickable = Boolean(detail.onClick);
                  const handleKeyDown = (e) => {
                    if (!isClickable) return;
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      detail.onClick();
                    }
                  };
                  return (
                    <div
                      className={`event-hero-detail ${isClickable ? 'clickable' : ''}`}
                      key={detail.label}
                      role={isClickable ? 'button' : undefined}
                      tabIndex={isClickable ? 0 : undefined}
                      onClick={detail.onClick}
                      onKeyDown={handleKeyDown}
                    >
                      <span className="detail-label">{detail.label}</span>
                      <span className="detail-value" style={detail.color ? { color: detail.color } : undefined}>
                        {detail.icon && <span className="detail-icon">{detail.icon}</span>}
                        {detail.value}
                        {detail.subValue && <span className="detail-subvalue">{detail.subValue}</span>}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        <section className="event-tickets-section">
          <div className="event-section-header">
            <div>
              <h2>Tickets &amp; Seating</h2>
              <span>
                Last updated {lastUpdated ? new Date(lastUpdated).toLocaleTimeString() : 'recently'}
                {refreshing ? ' • refreshing…' : ''}
              </span>
            </div>
          </div>

          {!isEventSalesOpen && (
            <div className="event-ticket-warning">
              <div className="warning-dot" />
              <div>
                <strong>Ticket sales are not open for this event.</strong>
                <p>{isEventViewOnly ? 'Sales are paused while the event is in view-only mode.' : 'This event is not currently selling tickets.'}</p>
              </div>
            </div>
          )}

          {ticketTypes.length === 0 ? (
            <div className="event-empty-state">
              <div className="icon"><Ticket size={48} /></div>
              <h3>No ticket types available</h3>
              <p>Check back soon for updated ticket information.</p>
            </div>
          ) : (
            <div className="event-ticket-list">
              {ticketTypes.map((ticketType) => {
                const isExpired = ticketType.validUntil && parseEventDate(ticketType.validUntil) < new Date();
                const isAvailable = isEventSalesOpen
                  && ticketType.status === 'ACTIVE'
                  && (ticketType.availableCount || 0) > 0
                  && !isExpired;
                const quantity = getQuantityInCart(ticketType.id);

                const availabilityLabel = getAvailabilityLabel(ticketType);
                const statusBadge = formatStatusBadge(ticketType.status);
                const qualifiesForGroup = ticketType.groupPackageAvailable
                  && ticketType.groupUnitPrice != null
                  && (ticketType.groupMinQuantity || 0) > 0
                  && quantity >= (ticketType.groupMinQuantity || 0);

                return (
                  <article
                    key={ticketType.id}
                    className={`event-ticket-card ${!isAvailable ? 'sold-out' : ''}`}
                  >
                    <div className="event-ticket-header">
                      <h3 className="event-ticket-title">{ticketType.name}</h3>
                      <div className="event-ticket-badges">
                        <span
                          className="event-ticket-badge"
                          style={{ color: statusBadge.color, borderColor: `${statusBadge.color}44`, background: `${statusBadge.color}22` }}
                        >
                          {statusBadge.label}
                        </span>
                        {ticketType.seatAssignment && (
                          <span className="event-ticket-badge">
                            {ticketType.seatAssignment === 'ASSIGNED'
                              ? 'Assigned seating'
                              : ticketType.seatAssignment.replace('_', ' ').toLowerCase()}
                          </span>
                        )}
                      </div>
                    </div>

                    {ticketType.description && <p className="event-ticket-description">{ticketType.description}</p>}

                    <div className="event-ticket-meta">
                      <div className={`event-ticket-price ${qualifiesForGroup ? 'discounted' : ''}`}>
                        {qualifiesForGroup ? (
                          <>
                            <span className="old-price">
                              {ticketType.currencyCode || 'USD'} {Number(ticketType.priceAmount || 0).toLocaleString()}
                            </span>
                            <span className="new-price">
                              {ticketType.currencyCode || 'USD'} {Number(ticketType.groupUnitPrice || 0).toLocaleString()}
                            </span>
                            <span className="group-note">Group rate applied</span>
                          </>
                        ) : (
                          <span>
                            {ticketType.currencyCode || 'USD'} {Number(ticketType.priceAmount || 0).toLocaleString()}
                          </span>
                        )}
                      </div>
                      <span className="event-ticket-availability">{availabilityLabel}</span>
                    </div>

                    {isAvailable ? (
                      ticketType.seatAssignment === 'ASSIGNED' ? (
                        <div className="event-ticket-actions">
                          <button
                            type="button"
                            className="event-ticket-button primary"
                            onClick={() => openSeatSelection(ticketType)}
                          >
                            Select Seats
                          </button>
                        </div>
                      ) : (
                        <div className="event-ticket-actions">
                          <div className="event-ticket-quantity">
                            <button
                              type="button"
                              className="event-ticket-qty-button"
                              onClick={() => handleUpdateQuantity(ticketType.id, quantity - 1)}
                              disabled={quantity <= 0}
                            >
                              –
                            </button>
                            <input
                              type="number"
                              min="0"
                              max={Math.min(10, ticketType.availableCount)}
                              value={quantity}
                              onChange={(event) => handleUpdateQuantity(ticketType.id, parseInt(event.target.value, 10) || 0)}
                            />
                            <button
                              type="button"
                              className="event-ticket-qty-button"
                              onClick={() => handleUpdateQuantity(ticketType.id, quantity + 1)}
                              disabled={quantity >= Math.min(10, ticketType.availableCount)}
                            >
                              +
                            </button>
                          </div>
                        </div>
                      )
                    ) : (
                      <div className="event-ticket-actions">
                        <button type="button" className="event-ticket-button danger" disabled>
                          {isEventSalesOpen ? 'Not available' : 'Sales not open'}
                        </button>
                      </div>
                    )}

                    {/* Seat Selection Widget Removed - Moved to Modal */}
                  </article>
                );
              })}
            </div>
          )}
        </section>

        {/* Group Packages Section */}
        {ticketTypes.some(tt => tt.groupPackageAvailable) && (
          <section className="event-tickets-section group-packages">
            <div className="event-section-header">
              <div>
                <h2><Package size={24} style={{ display: 'inline', marginRight: '10px', verticalAlign: 'middle' }} /> Group Deals</h2>
                <p className="section-subtitle">Bring your friends and unlock exclusive savings.</p>
              </div>
            </div>

            <div className="event-ticket-list">
              {ticketTypes.filter(tt => tt.groupPackageAvailable).map((ticketType) => {
                const isExpired = ticketType.validUntil && parseEventDate(ticketType.validUntil) < new Date();
                const isAvailable = isEventSalesOpen
                  && ticketType.status === 'ACTIVE'
                  && (ticketType.availableCount || 0) >= (ticketType.groupMinQuantity || 1)
                  && !isExpired;
                const quantity = getQuantityInCart(ticketType.id);

                const regularPrice = Number(ticketType.priceAmount || 0);
                const groupPrice = Number(ticketType.groupUnitPrice || 0);
                const savingsAmount = regularPrice - groupPrice;
                const savingsPercent = regularPrice > 0 ? Math.round((savingsAmount / regularPrice) * 100) : 0;

                return (
                  <article key={`group-${ticketType.id}`} className={`event-ticket-card group-card ${!isAvailable ? 'sold-out' : ''}`}>
                    <div className="event-ticket-header">
                      <h3 className="event-ticket-title">{ticketType.name} (Group Rate)</h3>
                      <div className="event-ticket-badges">
                        <span className="event-ticket-badge" style={{ background: '#e8f5e9', color: '#2e7d32', borderColor: '#c8e6c9' }}>
                          Save {savingsPercent}%
                        </span>
                        <span className="event-ticket-badge">
                          Min {ticketType.groupMinQuantity} tickets
                        </span>
                      </div>
                    </div>

                    <div className="event-ticket-description">
                      <p><strong>Save {ticketType.currencyCode}{savingsAmount.toLocaleString()} per ticket</strong> when you buy {ticketType.groupMinQuantity} or more.</p>
                      <p className="savings-text">
                        Group Price: <strong>{ticketType.currencyCode}{groupPrice.toLocaleString()}</strong> <span style={{ textDecoration: 'line-through', opacity: 0.7, marginLeft: '4px' }}>{ticketType.currencyCode}{regularPrice.toLocaleString()}</span>
                      </p>
                    </div>

                    <div className="event-ticket-actions">
                      <div className="event-ticket-quantity">
                        <button
                          type="button"
                          className="event-ticket-qty-button"
                          onClick={() => updateGroupQuantity(ticketType, quantity - 1)}
                          disabled={quantity <= 0}
                        >
                          –
                        </button>
                        <input
                          type="number"
                          min="0"
                          max={ticketType.availableCount}
                          value={quantity}
                          onChange={(e) => updateGroupQuantity(ticketType, parseInt(e.target.value, 10) || 0)}
                          style={{ width: '60px', textAlign: 'center' }}
                        />
                        <button
                          type="button"
                          className="event-ticket-qty-button"
                          onClick={() => updateGroupQuantity(ticketType, quantity + 1)}
                          disabled={quantity >= (ticketType.availableCount || 0)}
                        >
                          +
                        </button>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          </section>
        )}

        <SeatSelectionModal
          isOpen={!!activeSeatSelection}
          onClose={closeSeatSelection}
          event={event}
          ticketType={activeSeatSelection}
          onConfirmSelection={handleSeatsConfirmed}
          publicKey={process.env.REACT_APP_SEATSIO_PUBLIC_WORKSPACE_KEY}
        />


        <div className={`event-purchase-bar ${isPurchaseBarFixed ? 'is-fixed' : 'is-static'}`}>
          <div className="event-purchase-bar-inner">
            <div className="event-purchase-summary">
              <strong>Order Total</strong>
              <div className={`purchase-amount ${hasDiscount ? 'has-discount' : ''}`}>
                {hasDiscount && (
                  <span className="amount-original">
                    {activeCurrencySymbol}{originalTotal.toFixed(2)}
                  </span>
                )}
                <div className="amount-current">
                  <span className="currency-code">{activeCurrencyCode}</span>
                  <span className="currency-symbol">{activeCurrencySymbol}</span>
                  <span className="amount-value">
                    {discountedTotal > 0 ? discountedTotal.toFixed(2) : '0.00'}
                  </span>
                </div>
              </div>
            </div>
            <button type="button" className="event-purchase-button" onClick={buyNow}>
              Buy tickets
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventDetailsPage; 
